import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { toast } from 'sonner'
import { envValue } from '@/lib/env'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/Spinner'
import { BrandMark } from '@/components/BrandMark'
import { cn } from '@/lib/utils'
import { useAuth } from '@/hooks/useAuth'
import { businessSignupEnabled } from '@/lib/signupMode'
import { applyDocumentBranding } from '@/lib/documentBranding'
import {
  fetchPublicBranding,
  readRememberedTenantSlug,
  resolveSignInTenantSlug,
  type PublicBranding,
} from '@/lib/publicBranding'

// Password sign-in is the day-to-day path; the emailed link/code flow stays for
// accounts that have never set a password (invited staff and customers) and as
// the fallback when a password is forgotten.
type Mode = 'signin' | 'signup' | 'magic' | 'forgot'

/** Which "check your email" state to show after a send. */
type Sent = 'signup' | 'magic' | 'forgot' | null

const MIN_PASSWORD_LENGTH = 8

export default function LoginScreen() {
  const [mode, setMode] = useState<Mode>('signin')
  const [email, setEmail] = useState('')
  const [business, setBusiness] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [sent, setSent] = useState<Sent>(null)
  const [loading, setLoading] = useState(false)
  const {
    signInWithOtp,
    signInWithPassword,
    signUpWithPassword,
    requestPasswordReset,
  } = useAuth()
  const navigate = useNavigate()
  const { search } = useLocation()
  // A client's account unless this deployment sells the platform to businesses.
  const businessSignup = businessSignupEnabled()

  // Signed out, the tenant is unknown — resolved here from the URL, from the
  // last business this browser signed into, or from a single-tenant build's own
  // configuration. Cosmetic: sign-in works identically while this is null.
  const [brand, setBrand] = useState<PublicBranding | null>(null)
  useEffect(() => {
    const slug = resolveSignInTenantSlug(
      search,
      readRememberedTenantSlug(),
      envValue(process.env.EXPO_PUBLIC_TENANT_SLUG) ?? null,
    )
    if (!slug) return
    let cancelled = false
    fetchPublicBranding(slug).then((found) => {
      if (cancelled || !found) return
      setBrand(found)
      applyDocumentBranding(found.name, found.brand_logo_url, slug)
    })
    return () => { cancelled = true }
  }, [search])

  const trimmedEmail = email.trim().toLowerCase()

  const validEmail = () => {
    if (!trimmedEmail || !trimmedEmail.includes('@')) {
      toast.error('Invalid email', { description: 'Please enter a valid email address.' })
      return false
    }
    return true
  }

  const handleSignIn = async () => {
    if (!validEmail()) return
    if (!password) {
      toast.error('Password required', { description: 'Enter your password to sign in.' })
      return
    }
    setLoading(true)
    try {
      // The AuthGate owns the MFA decision (staff/owner only, needs the role
      // lookup) — landing on '/' lets it route to /mfa when required.
      await signInWithPassword(trimmedEmail, password)
      navigate('/', { replace: true })
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      if (/invalid login credentials/i.test(message)) {
        toast.error('Sign in failed', {
          description: 'Wrong email or password. If you’ve only ever signed in with email links, use “Email me a sign-in link” below, then set a password in Settings → Sign-in & security.',
        })
      } else if (/email not confirmed/i.test(message)) {
        toast.error('Email not verified', {
          description: 'Check your inbox for the verification link we sent when you signed up.',
        })
      } else {
        toast.error('Sign in failed', { description: message })
      }
    } finally {
      setLoading(false)
    }
  }

  const trimmedBusiness = business.trim()

  const handleSignUp = async () => {
    if (businessSignup && !trimmedBusiness) {
      toast.error('Business name required', { description: 'Tell us the name of your business to create its account.' })
      return
    }
    if (!validEmail()) return
    if (password.length < MIN_PASSWORD_LENGTH) {
      toast.error('Password too short', {
        description: `Use at least ${MIN_PASSWORD_LENGTH} characters.`,
      })
      return
    }
    if (password !== confirm) {
      toast.error('Passwords don’t match', { description: 'Both password fields must be identical.' })
      return
    }
    setLoading(true)
    try {
      const { needsVerification } = businessSignup
        ? await signUpWithPassword(trimmedEmail, password, trimmedBusiness)
        : await signUpWithPassword(trimmedEmail, password)
      if (needsVerification) {
        setSent('signup')
      } else {
        // Email confirmation disabled (local dev) — signed straight in.
        navigate('/', { replace: true })
      }
    } catch (err) {
      toast.error('Sign up failed', { description: err instanceof Error ? err.message : String(err) })
    } finally {
      setLoading(false)
    }
  }

  const handleSendLink = async () => {
    if (!validEmail()) return
    setLoading(true)
    try {
      await signInWithOtp(trimmedEmail)
      setSent('magic')
    } catch (err) {
      toast.error('Error', { description: String(err) })
    } finally {
      setLoading(false)
    }
  }

  const handleForgot = async () => {
    if (!validEmail()) return
    setLoading(true)
    try {
      await requestPasswordReset(trimmedEmail)
      setSent('forgot')
    } catch (err) {
      toast.error('Error', { description: String(err) })
    } finally {
      setLoading(false)
    }
  }

  const switchMode = (next: Mode) => {
    setMode(next)
    setSent(null)
    setBusiness('')
    setPassword('')
    setConfirm('')
  }

  const inputClass = 'h-auto rounded-xl border-slate-700 bg-slate-900 px-4 py-3.5 text-base text-slate-50 placeholder:text-slate-400 md:text-base dark:bg-slate-900'

  const primaryButton = (label: string, onClick: () => void, disabled: boolean) => (
    <button
      type="button"
      className={cn(
        'flex items-center justify-center rounded-xl bg-blue-500 py-3.5',
        (loading || disabled) && 'opacity-60',
      )}
      onClick={onClick}
      disabled={loading || disabled}
      aria-label={label}
    >
      {loading ? (
        <Spinner size={20} className="text-white" />
      ) : (
        <span className="text-base font-semibold text-white">{label}</span>
      )}
    </button>
  )

  const linkButton = (label: string, onClick: () => void, subtle = false) => (
    <button type="button" className="flex items-center justify-center py-1" onClick={onClick}>
      <span className={cn('underline', subtle ? 'text-xs text-slate-600' : 'text-[13px] text-slate-400')}>
        {label}
      </span>
    </button>
  )

  const SENT_COPY: Record<Exclude<Sent, null>, { title: string; detail: string }> = {
    signup: {
      title: 'Verify your email',
      detail: 'Click the verification link in the email to activate your account, then sign in.',
    },
    magic: {
      title: 'Check your email',
      detail: 'Click the link in the email to sign in. It may take a minute to arrive.',
    },
    forgot: {
      title: 'Check your email',
      detail: 'Click the link in the email to choose a new password.',
    },
  }

  return (
    <div className="flex min-h-screen flex-1 flex-col bg-slate-900">
      <div className="flex min-h-0 flex-1 flex-col justify-center gap-10 px-6">
        {/* Logo / branding. Falls back to the platform identity when the tenant
            cannot be worked out — an honest default, not a failure state. */}
        <div className="flex flex-col items-center gap-3">
          <BrandMark
            name={brand?.name ?? 'Porter'}
            logoUrl={brand?.brand_logo_url}
            color={brand?.brand_primary_color || '#3b82f6'}
            size={72}
            shape="squircle"
          />
          <p className="text-[26px] font-bold tracking-[-0.5px] text-white">
            {brand?.name ?? 'Porter'}
          </p>
          <p className="text-sm text-slate-400">
            {brand?.brand_tagline ?? 'AI-first booking management'}
          </p>
        </div>

        {sent ? (
          /* ── Email sent state (signup verification / magic link / reset) ── */
          <div className="flex flex-col gap-4 rounded-[20px] bg-slate-800 p-6">
            <p className="text-center text-[40px]">✉️</p>
            <h1 className="text-[22px] font-bold text-slate-50">{SENT_COPY[sent].title}</h1>
            <p className="text-sm leading-5 text-slate-400">
              We sent an email to<br />
              <span className="font-semibold text-blue-500">{email}</span>
            </p>
            <p className="text-center text-[13px] leading-[19px] text-slate-500">
              {SENT_COPY[sent].detail}
            </p>
            {sent === 'magic' && linkButton(
              'Have a code instead? Enter it here',
              () => navigate({ pathname: '/verify', search: `?${new URLSearchParams({ email }).toString()}` }),
              true,
            )}
            {linkButton('Back to sign in', () => switchMode('signin'))}
          </div>
        ) : (
          <div className="flex flex-col gap-4 rounded-[20px] bg-slate-800 p-6">
            <h1 className="text-[22px] font-bold text-slate-50">
              {mode === 'signup' ? (businessSignup ? 'Create your business' : 'Create your account')
                : mode === 'magic' ? 'Email me a sign-in link'
                : mode === 'forgot' ? 'Reset your password'
                : 'Welcome back'}
            </h1>
            <p className="text-sm leading-5 text-slate-400">
              {mode === 'signup'
                ? (businessSignup
                  ? 'Set up a new business account — we’ll email you a verification link to activate it.'
                  : `Use the email you gave ${brand?.name ?? 'us'} when you booked — your appointments are linked to it. We’ll email you a link to verify it.`)
                : mode === 'magic' ? 'No password needed — we’ll email you a one-time sign-in link.'
                : mode === 'forgot' ? 'We’ll email you a link to choose a new password.'
                : 'Sign in with your email and password.'}
            </p>

            {mode === 'signup' && businessSignup && (
              <Input
                className={cn('mt-1', inputClass)}
                value={business}
                onChange={e => setBusiness(e.target.value)}
                placeholder="Business name"
                type="text"
                name="organization"
                autoCapitalize="words"
                autoComplete="organization"
                disabled={loading}
              />
            )}

            <Input
              className={cn(mode === 'signup' && businessSignup ? '' : 'mt-1', inputClass)}
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              type="email"
              name="email"
              autoCapitalize="none"
              autoComplete="email"
              autoCorrect="off"
              disabled={loading}
            />

            {(mode === 'signin' || mode === 'signup') && (
              <Input
                className={inputClass}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder={mode === 'signup' ? `Password (min ${MIN_PASSWORD_LENGTH} characters)` : 'Password'}
                type="password"
                name={mode === 'signup' ? 'new-password' : 'current-password'}
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                onKeyDown={e => { if (e.key === 'Enter' && mode === 'signin') handleSignIn() }}
                disabled={loading}
              />
            )}

            {mode === 'signup' && (
              <Input
                className={inputClass}
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                placeholder="Confirm password"
                type="password"
                name="confirm-password"
                autoComplete="new-password"
                onKeyDown={e => { if (e.key === 'Enter') handleSignUp() }}
                disabled={loading}
              />
            )}

            {mode === 'signin' && primaryButton('Sign In', handleSignIn, !trimmedEmail || !password)}
            {mode === 'signup' && primaryButton('Create Account', handleSignUp, (businessSignup && !trimmedBusiness) || !trimmedEmail || !password || !confirm)}
            {mode === 'magic' && primaryButton('Send Sign-in Link', handleSendLink, !trimmedEmail)}
            {mode === 'forgot' && primaryButton('Send Reset Link', handleForgot, !trimmedEmail)}

            {mode === 'signin' && (
              <>
                {linkButton('Forgot password?', () => switchMode('forgot'))}
                {linkButton('Email me a sign-in link instead', () => switchMode('magic'))}
                {linkButton('New here? Create an account', () => switchMode('signup'))}
              </>
            )}
            {mode !== 'signin' && linkButton('Back to sign in', () => switchMode('signin'))}
          </div>
        )}

        <div className="flex items-center justify-center gap-2 py-1">
          <button type="button" onClick={() => navigate('/privacy')}>
            <span className="text-xs text-slate-600 underline">Privacy Policy</span>
          </button>
          <span className="text-xs text-slate-700">·</span>
          <button type="button" onClick={() => navigate('/terms')}>
            <span className="text-xs text-slate-600 underline">Terms of Service</span>
          </button>
        </div>
      </div>
    </div>
  )
}
